/**
 * The following enum and utility types give us strongly-typed
 * LaunchDarkly flag values. When adding a new flag, two steps
 * must be taken to ensure the correct type is associated with the flag
 */
/**
 * First update this enum to include the flag name
 * Note: the flag name should appear exactly as is it is in the
 * Launch Darkly dashboard
 */

export enum LaunchDarklyFlag {
  AUTO_FILL_TEST_CARD = 'autoFillTestCard',
  APP_TAKEOVER = 'app-takeover',
  CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH = 'curbsideVehicleDescriptionMaxLength',
  DEFAULT_SERVICE_MODE_IN_STORE = 'defaultServiceModeInStore',
  DELIVERY_RADIUS_IN_MILES = 'deliveryRadiusInMiles',
  DISABLE_DINE_IN = 'disableDineIn',
  DISABLE_DRIVE_THRU = 'disableDriveThru',
  DISABLE_TAKE_OUT = 'disableTakeOut',
  ENABLE_ACCOUNT_DELETE_ONE_TRUST = 'enableAccountDeleteOneTrust',
  ENABLE_ADD_EXTRAS = 'enable-add-extras',
  ENABLE_ADS_PERSONALIZATION_OPT_OUT = 'enable-ads-personalization-opt-out',
  ENABLE_ALLERGENS_DISCLAIMER = 'enableAllergensDisclaimer',
  ENABLE_FEATURE_ITEM_NUTRITION_INGREDIENTS = 'enable-feature-item-nutrition-ingredients',
  ENABLE_APPLE_PAY = 'enableApplePay',
  ENABLE_APPLE_PAY_WEB = 'enable-apple-pay-web',
  ENABLE_AUTO_SELECT_RESTAURANT = 'enableAutoSelectRestaurant',
  ENABLE_CASH_APP_PAYMENTS = 'enable-cash-app-payments',
  ENABLE_CASH_PAYMENT = 'enable-cash-payment',
  ENABLE_CLAIM_LOYALTY_POINTS = 'enable-claim-loyalty-points',
  ENABLE_COGNITO_SIGNUP_IN_BE = 'enableCognitoSignupInBe',
  ENABLE_CUSTOM_BIRTHDAY_INPUT = 'enableCustomBirthdayInput',
  ENABLE_DAY_PARTS_PER_STORE = 'enable-dayparts-per-store',
  ENABLE_DELIVERY = 'delivery',
  ENABLE_EDIT_OFFERS_CART_BUTTON = 'enable-edit-offers-cart-button',
  ENABLE_FEATURE_ONBOARDING = 'enable-feature-onboarding',
  ENABLE_REWARD_UNSTACKING = 'enable-rewards-stacking-evaluation',
  ENABLE_REWARD_COOLDOWN = 'enable-rewards-cooldown-evaluation',
  ENABLE_SMS_MARKETING_SUBSCRIPTIONS = 'enable-sms-marketing-subscriptions',
  ENABLE_UNLINK_WALMART_FLOW = 'enable-unlink-walmart-flow',
  /**
   * Whether or not to require address line 2 in the delivery details form
   * - Some markets, like BK DE, require this to identify the customer
   */
  ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2 = 'enable-delivery-details-form-require-address-2',
  ENABLE_DISCOUNTS_ON_ORDER_RECEIPT = 'enable-discounts-on-order-receipt',
  ENABLE_DOORDASH_DRIVE_SUGGESTIONS = 'enable-doordash-drive-address-suggestions',
  ENABLE_FEES_ON_ORDER_RECEIPT = 'enable-fees-on-order-receipt',
  ENABLE_FIND_RESTAURANT_LINK = 'enable-find-restaurant-link',
  ENABLE_FORCE_PICKER_IMAGE = 'product-details-force-picker-image',
  ENABLE_FORCED_NATIVE_APP_UPDATE = 'enable-forced-native-app-update',
  ENABLE_FOODWARE_ITEMS = 'enable-foodware-items',
  ENABLE_FORTER_FRAUD = 'enable-forter-fraud',
  ENABLE_FREE_DELIVERY_CHECKOUT_PROGRESS_BAR = 'enableFreeDeliveryCheckoutProgressBar',
  ENABLE_GIFT_CARD = 'gift-card-payment-method',
  ENABLE_GLOBAL_OFFERS_COOLDOWN = 'enable-global-offers-cooldown',
  ENABLE_GOOGLE_PAY = 'enableGooglePay',
  ENABLE_GOOGLE_PAY_WEB = 'enable-google-pay-web',
  ENABLE_IN_RESTAURANT_VARIATIONS = 'enable-in-restaurant-variations',
  ENABLE_INTERNAL_DIAGNOSTIC_TOOLS = 'enableInternalDiagnosticTools',
  ENABLE_LOCATOR_CLEAR_STORE_AND_SM_BUTTON = 'enable-locator-clear-store-and-sm-button',
  ENABLE_LOYALTY = 'enable-loyalty-fe',
  ENABLE_LOYALTY_BADGES = 'enable-loyalty-badges',
  ENABLE_LOYALTY_CELEBRATIONS = 'enable-loyalty-celebrations',
  ENABLE_LOYALTY_OFFERS = 'enable-loyalty-offers',
  ENABLE_LOYALTY_POINTS_IN_HEADER = 'enable-loyalty-points-in-header',
  ENABLE_LOYALTY_PROFILE = 'enable-loyalty-profile',
  ENABLE_LOYALTY_PROMO_CODES = 'enable-loyalty-promo-codes',
  ENABLE_LOYALTY_QR_CODE_LOYALTY_ID = 'enable-qr-code-loyalty-id',
  ENABLE_LOYALTY_OFFER_EXPIRATION_DISPLAY = 'enable-offer-expiration-display',
  ENABLE_LOYALTY_STANDARD_OFFERS = 'enable-loyalty-standard-offers',
  ENABLE_MOVE_SIGNUP_AFTER_CART = 'enable-move-signup-after-cart',
  ENABLE_MY_CODE_PAYMENT_METHOD = 'enable-my-code-payment-method',
  ENABLE_NUTRITION_EXPLORER_ALLERGENS = 'enable-nutrition-explorer-allergens',
  ENABLE_OFFERS = 'enableOffers',
  ENABLE_OFFERS_EDIT_CART = 'enable-offers-edit-cart',
  ENABLE_ORDER_NUMBER_GENERATION = 'enable-order-number-generation',
  ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE = 'enable-menu-reordering-availability-override',
  ENABLE_PAYMENT_ERROR_CONTINUE = 'enable-payment-error-continue',
  ENABLE_PAYPAL_PAYMENTS = 'enable-paypal-payments',
  ENABLE_PYPESTREAM = 'enable-pypestream',
  ENABLE_PRICE_MISMATCH_INTERNAL_ONLY = 'enable-price-mismatch-internal-only',
  ENABLE_QUEST_TILES = 'enable-quest-tiles',
  ENABLE_REORDER = 'enable-reorder',
  ENABLE_SIGN_UP = 'enableSignUp',
  ENABLE_SIGNUP_CART_BUTTON_TEXT = 'enable-signup-cart-button-text',
  ENABLE_SORT_OFFERS_FOR_SERVICE_MODE = 'enable-sort-offers-for-service-mode',
  ENABLE_STORE_CONFIRMATION_MODAL = 'enableStoreConfirmationModal',
  ENABLE_DELIVERY_FIRST_QUOTE_V2 = 'enable-delivery-first-quote-v2',
  ENABLE_USER_JOURNEY_CHALLENGE = 'enable-profile-completion-challenge',
  /**
   * when set to true, execute the standalone addPaymentMethod mutation
   * that occurs as part of the checkout process when the 'save payment method' checkbox is selected
   */
  ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT = 'enable-vault-after-making-one-time-payment',
  ENABLE_VENMO_PAYMENTS = 'enable-venmo-payments',
  ENABLE_WALMART_PLUS_INTEGRATION = 'enable-walmart-plus-integration',
  ENABLE_WALMART_PLUS_TRIAL_OFFER = 'enable-walmart-plus-trial-offer',
  ENABLE_WALMART_PLUS_TRIAL_OFFER_MONTHLY = 'enable-walmart-plus-trial-offer-monthly',
  ENABLE_ZERO_BOUNCE_ERROR_MODAL = 'enable-zero-bounce-error-modal',
  ENABLE_SODIUM_WARNING = 'enable-sodium-warning',
  FIRE_ORDER_AHEAD = 'fireOrderAhead',
  FORCE_RESTAURANT_SELECTION_FOR_REWARDS = 'force-restaurant-selection-for-rewards',
  HIDE_ITEMS_FROM_NUTRITION_INFO = 'hide-items-from-nutrition-info',
  LOYALTY_IN_RESTAURANT_STATUS_POLL_INTERVAL_MS = 'loyalty-in-restaurant-status-poll-interval-ms',
  ORDER_LOCATOR_TABS = 'order-locator-tabs',
  OVERRIDE_CHECKOUT_DELIVERY_MINIMUM = 'checkoutDeliveryMinimumOverrideCents',
  PAYMENT_FIELD_VARIATIONS = 'payment-fields-variations',
  SUPPORTED_CARD_BRANDS_VARIATIONS = 'supported-card-brands-variations',
  RADIUS_FOR_STORE_LOCATOR = 'radius-for-store-locator',
  RESET_CART_RESET_STORE_TIMEOUT = 'resetStoreResetCartTimeout',
  RESET_CART_TIMEOUT_HRS = 'reset-cart-timeout-hrs',
  SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT = 'send-postal-code-only-for-first-data-payment',
  SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE = 'show-braze-content-cards-on-homepage',
  SHOW_INCREASED_DELIVERY_FEES_MODAL = 'show-increased-delivery-fees-modal',
  SHOW_ORDERING_ISSUE = 'show-ordering-issue-info',
  SIGN_UP_FIELDS_VARIATIONS = 'sign-up-fields-variations',
  SKIP_LOYALTY_STORE_VALIDATION = 'skip-loyalty-store-validation',
  STORE_CARD_BUTTON_OPTIONS = 'store-card-button-options',
  TIERED_DELIVERY_FEES = 'tiered-delivery-fees',
  SANITY_PROJECT_ID = 'sanity-project-id',
  TIP_PERCENT_THRESHOLD_CENTS = 'tipPercentThresholdCents',
  ENABLE_PREPAID_PAYMENT_METHODS = 'enable-prepaid-payment-methods',
  // ****** TEST FLAGS ******
  // These flags may not exist in LD but allows us to enable their functionality
  // by using ?rbiFeature.$FLAG=true on the url such as rbiFeature.enable-test-google-pay-web=true
  ENABLE_COOLDOWN_TOAST_INFO = 'enable-cooldown-toast-info',
  ENABLE_MAIN_FAB_BUTTON = 'enable-main-fab-button',
  ENABLE_TEST_APPLE_PAY_WEB = 'enable-test-apple-pay-web',
  ENABLE_TEST_GOOGLE_PAY_WEB = 'enable-test-google-pay-web',
  /*
   Load static Sanity page instead of default loyalty component
   */
  ENABLE_OFFERS_STATIC_PAGE = 'enable-offers-static-page',
  MAX_RBI_API_RETRIES = 'max-rbi-api-retries',
  MAX_SANITY_API_RETRIES = 'max-sanity-api-retries',

  ENABLE_FAST_LANE_DRIVE_THRU = 'enable-fast-lane-drive-thru',
  ENABLE_OPTIONAL_AMPLITUDE_TRACKING = 'enable-optional-amplitude-tracking',
  ENABLE_DEBUG_TOOL = 'enable-debug-menu',

  /**
   * Replace "Drive-Thru" text with "Mobile Order Drive-Thru Lane"
   */
  ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE = 'enable-mobile-order-drive-thru-lane',
  /**
   * Loyalty Id and Pay Feature Flag
   */
  ENABLE_ID_AND_PAY_FE = 'enable-id-and-pay-fe',

  /**
   * Enable Social Login Google Feature Flag
   */
  ENABLE_SOCIAL_LOGIN_GOOGLE = 'enable-social-login-google',

  /**
   * Enable Social Login Apple Feature Flag
   */
  ENABLE_SOCIAL_LOGIN_APPLE = 'enable-social-login-apple',

  /**
   * Enable Order Injection Flow
   */
  ORDER_INJECTION_FLOW = 'order-injection-flow',

  /**
   * Enable top order status bar
   */
  ENABLE_TOP_ORDER_STATUS_BAR = 'enable-top-order-status-bar',

  /**
   * Enable Foundation Donations
   */
  ENABLE_FOUNDATION_DONATIONS = 'enable-foundation-donations',

  /**
   * Enable OTP Login
   */
  ENABLE_OTP_LOGIN = 'enabled-otp-login',

  /**
   * Enable Delivery Fees Stripe
   */
  DELIVERY_FEES_STRIPE = 'delivery-fees-stripe',

  /**
   * Enable User Away From Store
   */
  USER_AWAY_FROM_STORE = 'user-away-from-store',

  /**
   * Enable Non Traditional Store UI
   */
  ENABLE_NON_TRADITIONAL_STORE_UI = 'enable-non-traditional-store-ui',

  /**
   * Enable IOS Live Activity
   */
  ENABLE_IOS_LIVE_ACTIVITY = 'enable-ios-live-activity',

  /**
   * Enable cart cancellation policy
   */
  ENABLE_CART_CANCELLATION_POLICY = 'enable-cart-cancellation-policy',

  /**
   * Commit order manual test: when set to a non-empty string (e.g. "Code405" or "COMMIT_ORDER_COOLING_PERIOD"),
   * the CommitOrder GraphQL request is intercepted and the specified error is returned
   * to trigger the submit error modal. Used for QA/manual testing.
   */
  COMMIT_ORDER_TEST_ERROR_MODAL = 'commit-order-test-error-modal',

  /**
   * Controls whether or not to display a prompt to the user asking them
   * if they want to change the selected store to another that is closer.
   */
  ENABLE_AUTO_SELECT_CLOSEST_STORE_PROMPT = 'enable-auto-select-closest-store-prompt',

  /**
   * Controls on which items should we show the "NEW" badge on the first section of the account page.
   */
  ENABLE_ACCOUNT_NEW_BADGE = 'enable-account-new-badge',

  /**
   * Controls the time that Order Status Bar will be shown
   */
  TOP_ORDER_STATUS_BAR_TIME_OUT = 'top-order-status-bar-time-out',

  /**
   * Controls the time that Auto Select Store Prompt will be shown
   */
  AUTO_SELECT_STORE_PROMPT_TIMEOUT = 'auto-select-store-prompt-timeout',

  /**
   * Enable web view debugging
   */
  ENABLE_WEBVIEW_DEBUGGING = 'enable-webview-debugging',

  /**
   * Enable Mobile Order Drive Thru Service Mode
   */
  ENABLE_MOBILE_ORDER_DRIVE_THRU = 'enable-mobile-drive-thru-service-mode',

  /**
   * Enable Smart Alerts
   */
  ENABLE_SMART_ALERTS = 'enable-smart-alerts',

  /**
   * Stores that have Mobile Order Drive Thru Service Mode enabled
   */
  MOBILE_ORDER_DRIVE_THRU_STORES = 'mobile-order-drive-thru-stores',

  /**
   * Enables or disables the NYC regulatory link in the user interface. This feature is only available for users in NYC.
   */
  NYC_REGULATORY_LINK = 'nyc-regulatory-link',

  /**
   * Enables showing seasonal promo on Walmart page
   */
  SHOW_WALMART_SEASONAL_PROMO = 'show-walmart-seasonal-promo',
  /**
   * Enable offer groups v2
   */
  ENABLE_OFFER_GROUPS_V2 = 'enable-offer-groups-v2',

  /**
   * Enables rendering of Loyalty Engine Personalized Offer Groups (POGs) on the Offers screen.
   * Default should be false for controlled rollout.
   */
  ENABLE_LOYALTY_PERSONALIZED_OFFER_GROUPS = 'enable-loyalty-personalized-offer-groups',

  /**
   * Show Spotify Partnership
   */
  SHOW_SPOTIFY_PARTNERSHIP = 'show-spotify-partnership',

  /**
   * Enable Apple MapKit JS for Web maps rendering instead of Google Maps
   * When enabled, uses Apple MapKit JS for map rendering on web
   */
  ENABLE_APPLE_MAPKIT_WEB = 'enable-apple-mapkit-web',

  /**
   * Expose Digital Gift Card Number After Adding Physical Card
   */
  IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED = 'is-digital-gift-card-number-exposed-enabled',

  /**
   * When enabled, fetch offers from the loyalty-service backend
   * instead of the legacy CMS + loyalty engine pipeline.
   */
  ENABLE_LOYALTY_SERVICE_OFFERS = 'enable-loyalty-service-offers',

  /**
   * When enabled, fetch rewards from the loyalty-service backend
   * instead of the legacy CMS + loyalty engine pipeline.
   */
  ENABLE_LOYALTY_SERVICE_REWARDS = 'enable-loyalty-service-rewards',

  /**
   * When enabled, create in-restaurant order selections through loyalty-service
   * instead of the legacy loyalty engine mutation.
   */
  ENABLE_LOYALTY_SERVICE_ORDER_SELECTION = 'enable-loyalty-service-order-selection',
}

export type AllLaunchDarklyFlags = {
  [key in LaunchDarklyFlag]: any;
};

/**
 * Secondly, update the appropriate union based on the type
 * your new flag should return
 */
export type NumericFlags =
  | LaunchDarklyFlag.CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH
  | LaunchDarklyFlag.FIRE_ORDER_AHEAD
  | LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM
  | LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES
  | LaunchDarklyFlag.RESET_CART_RESET_STORE_TIMEOUT
  | LaunchDarklyFlag.TIP_PERCENT_THRESHOLD_CENTS
  | LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR
  | LaunchDarklyFlag.LOYALTY_IN_RESTAURANT_STATUS_POLL_INTERVAL_MS
  | LaunchDarklyFlag.RESET_CART_TIMEOUT_HRS
  | LaunchDarklyFlag.MAX_RBI_API_RETRIES
  | LaunchDarklyFlag.MAX_SANITY_API_RETRIES
  | LaunchDarklyFlag.USER_AWAY_FROM_STORE
  | LaunchDarklyFlag.TOP_ORDER_STATUS_BAR_TIME_OUT
  | LaunchDarklyFlag.AUTO_SELECT_STORE_PROMPT_TIMEOUT;

export type StringFlags =
  | LaunchDarklyFlag.DEFAULT_SERVICE_MODE_IN_STORE
  | LaunchDarklyFlag.ENABLE_FORCED_NATIVE_APP_UPDATE
  | LaunchDarklyFlag.ORDER_INJECTION_FLOW
  | LaunchDarklyFlag.DELIVERY_FEES_STRIPE
  | LaunchDarklyFlag.ENABLE_ACCOUNT_NEW_BADGE
  | LaunchDarklyFlag.MOBILE_ORDER_DRIVE_THRU_STORES
  | LaunchDarklyFlag.NYC_REGULATORY_LINK
  | LaunchDarklyFlag.SANITY_PROJECT_ID
  | LaunchDarklyFlag.COMMIT_ORDER_TEST_ERROR_MODAL;

export type VariationFlags =
  | LaunchDarklyFlag.ENABLE_IN_RESTAURANT_VARIATIONS
  | LaunchDarklyFlag.ENABLE_PREPAID_PAYMENT_METHODS
  | LaunchDarklyFlag.HIDE_ITEMS_FROM_NUTRITION_INFO
  | LaunchDarklyFlag.ORDER_LOCATOR_TABS
  | LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS
  | LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS
  | LaunchDarklyFlag.SIGN_UP_FIELDS_VARIATIONS
  | LaunchDarklyFlag.STORE_CARD_BUTTON_OPTIONS
  | LaunchDarklyFlag.APP_TAKEOVER
  | LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE;

export type BooleanFlags = Exclude<LaunchDarklyFlag, NumericFlags | StringFlags | VariationFlags>;

export type BoolFlags =
  | LaunchDarklyFlag.ENABLE_ADS_PERSONALIZATION_OPT_OUT
  | LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN
  | LaunchDarklyFlag.ENABLE_ORDER_NUMBER_GENERATION
  | LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  | LaunchDarklyFlag.ENABLE_DELIVERY_FIRST_QUOTE_V2
  | LaunchDarklyFlag.ENABLE_FEATURE_ITEM_NUTRITION_INGREDIENTS
  | LaunchDarklyFlag.ENABLE_OFFERS_STATIC_PAGE
  | LaunchDarklyFlag.ENABLE_DISCOUNTS_ON_ORDER_RECEIPT
  | LaunchDarklyFlag.ENABLE_FEES_ON_ORDER_RECEIPT
  | LaunchDarklyFlag.ENABLE_QUEST_TILES
  | LaunchDarklyFlag.ENABLE_FEATURE_ONBOARDING
  | LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU
  | LaunchDarklyFlag.ENABLE_DEBUG_TOOL
  | LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU
  | LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  | LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS
  | LaunchDarklyFlag.ENABLE_UNLINK_WALMART_FLOW
  | LaunchDarklyFlag.ENABLE_TOP_ORDER_STATUS_BAR
  | LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION
  | LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER
  | LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER_MONTHLY
  | LaunchDarklyFlag.ENABLE_AUTO_SELECT_CLOSEST_STORE_PROMPT
  | LaunchDarklyFlag.ENABLE_EDIT_OFFERS_CART_BUTTON
  | LaunchDarklyFlag.ENABLE_SMART_ALERTS
  | LaunchDarklyFlag.SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE
  | LaunchDarklyFlag.ENABLE_OFFER_GROUPS_V2
  | LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS
  | LaunchDarklyFlag.ENABLE_DOORDASH_DRIVE_SUGGESTIONS
  | LaunchDarklyFlag.ENABLE_APPLE_MAPKIT_WEB
  | LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED;

export type FlagType<F extends LaunchDarklyFlag> = F extends NumericFlags
  ? number
  : F extends StringFlags
  ? string
  : F extends BooleanFlags
  ? boolean
  : F extends VariationFlags
  ? unknown
  : never;

export type LaunchDarklyFlagsObject = { [F in LaunchDarklyFlag]?: FlagType<F> };
